import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  type Checklist,
  STORE_STATUS_LABEL,
  checklistSummary,
  storeStatusTone,
} from '../../features/onboarding/onboardingChecklist';
import {
  Button,
  Callout,
  Dialog,
  EmptyState,
  IconCheckCircle,
  IconRefresh,
  IconStore,
  ListPage,
  Row,
  Spinner,
  StatusLabel,
  Table,
  TBody,
  TD,
  TH,
  THead,
  TR,
} from '../../ui/v2';
import { api, apiEndpoints } from '../../utils/apiService';
import { type TableSortState, toggleSortState } from '../../utils/listOrdering';
import OnboardingChecklistAdmin from './OnboardingChecklistAdmin';
import { TableSearch } from './TableSearch';
import { filterAndSortRows, rowSearchText } from './clientTable';
import {
  type StoreOnboardingStatus,
  onboardingIssueCount,
  onboardingStatusLabel,
  onboardingStatusTone,
  onboardingStatusesFrom,
} from './onboardingStatusModel';

const StatusDetailsDialog: React.FC<{
  open: boolean;
  status: StoreOnboardingStatus | null;
  onClose: () => void;
  onChecklistChange: (checklist: Checklist) => void;
}> = ({ open, status, onClose, onChecklistChange }) => (
  <Dialog
    open={open}
    onClose={onClose}
    title={`Onboarding Status: ${status?.store_name ?? ''}`}
    size="lg"
    footer={<Button onClick={onClose}>Close</Button>}
  >
    {status && (
      <div className="flex flex-col gap-4">
        <dl>
          <Row label="Store ID">
            <span className="v2-num">{status.store_id}</span>
          </Row>
          <Row label="Status">
            <StatusLabel tone={onboardingStatusTone(status)}>
              {onboardingStatusLabel(status)}
            </StatusLabel>
          </Row>
        </dl>
        {status.store_id > 0 && (
          <OnboardingChecklistAdmin
            storeId={status.store_id}
            onChange={onChecklistChange}
          />
        )}
        {status.issues && status.issues.length > 0 ? (
          <section aria-labelledby="onboarding-issues-heading">
            <h3
              id="onboarding-issues-heading"
              className="mb-2 text-v2-13 font-semibold text-v2-fg"
            >
              Issues to Resolve ({status.issues.length}):
            </h3>
            <ul className="flex flex-col gap-1.5">
              {status.issues.map((issue, index) => (
                <li key={index}>
                  <Callout tone="warning">{issue}</Callout>
                </li>
              ))}
            </ul>
          </section>
        ) : (
          <Callout
            tone="accent"
            icon={<IconCheckCircle size={14} />}
            title="All onboarding requirements are complete!"
          />
        )}
      </div>
    )}
  </Dialog>
);

const stageOf = ({ checklist }: StoreOnboardingStatus) =>
  checklist ? STORE_STATUS_LABEL[checklist.status] : '';

const progressOf = ({ checklist }: StoreOnboardingStatus) =>
  checklist ? `${checklist.counts.done}/${checklist.counts.total}` : '';

export default function OnboardingStatusTab() {
  const [statuses, setStatuses] = useState<StoreOnboardingStatus[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [selected, setSelected] = useState<StoreOnboardingStatus | null>(null);
  const [isDetailsOpen, setIsDetailsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState<
    TableSortState<
      'store_name' | 'store_id' | 'stage' | 'progress' | 'status' | 'issues'
    >
  >({ key: null, direction: 'asc' });
  const visibleStatuses = useMemo(
    () =>
      filterAndSortRows(
        statuses,
        search,
        (status) =>
          rowSearchText([
            status.store_name,
            status.store_id,
            stageOf(status),
            onboardingStatusLabel(status),
            onboardingIssueCount(status),
          ]),
        sort,
        (status, key) => {
          if (key === 'store_name') return status.store_name;
          if (key === 'store_id') return status.store_id;
          if (key === 'stage') return stageOf(status);
          if (key === 'progress') return status.checklist?.counts.done ?? -1;
          if (key === 'status') return onboardingStatusLabel(status);
          return onboardingIssueCount(status);
        },
      ),
    [search, sort, statuses],
  );
  const filtering = search.trim().length > 0;

  const fetchStatuses = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const response = await api.get(apiEndpoints.getOnboardingStatus());
      if (response && response.ok) {
        setStatuses(onboardingStatusesFrom(await response.json()));
      } else {
        setError('Failed to fetch onboarding statuses');
        setStatuses([]);
      }
    } catch (fetchError) {
      console.error('Failed to fetch onboarding statuses:', fetchError);
      setError('An error occurred while fetching onboarding statuses');
      setStatuses([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void fetchStatuses();
  }, [fetchStatuses]);

  // Keep Stage and Progress in step with changes made in the dialog.
  const updateChecklist = useCallback((checklist: Checklist) => {
    setStatuses((rows) =>
      rows.map((row) =>
        row.store_id === checklist.store_id
          ? { ...row, checklist: checklistSummary(checklist) }
          : row,
      ),
    );
  }, []);

  const openDetails = (status: StoreOnboardingStatus) => {
    setSelected(status);
    setIsDetailsOpen(true);
  };

  const showTable = !loading && !error && visibleStatuses.length > 0;

  let body: React.ReactNode;
  if (loading) {
    body = (
      <div
        role="status"
        className="flex min-h-[240px] items-center justify-center gap-2 text-v2-13 text-v2-fg-3"
      >
        <Spinner />
        Loading onboarding statuses...
      </div>
    );
  } else if (error) {
    body = (
      <div className="p-4 lg:p-6">
        <Callout tone="danger">{error}</Callout>
      </div>
    );
  } else if (showTable) {
    body = (
      <div className="v2-scroll overflow-x-auto">
        <Table fixed className="min-w-[880px]">
          <THead>
            <tr>
              {(
                [
                  ['store_name', 'Store Name', undefined],
                  ['store_id', 'Store ID', 110],
                  ['stage', 'Stage', 150],
                  ['progress', 'Progress', 110],
                  ['status', 'Internal checks', 150],
                  ['issues', 'Issues Count', 130],
                ] as const
              ).map(([key, label, width]) => (
                <TH
                  key={key}
                  width={width}
                  sortable
                  active={sort.key === key}
                  dir={sort.direction}
                  onSort={() =>
                    setSort((current) => toggleSortState(current, key))
                  }
                >
                  {label}
                </TH>
              ))}
            </tr>
          </THead>
          <TBody>
            {visibleStatuses.map((status, idx) => (
              <TR
                key={status.store_id || idx}
                onActivate={() => openDetails(status)}
              >
                <TD truncate className="font-medium">
                  {status.store_name || 'N/A'}
                </TD>
                <TD num>{status.store_id || 'N/A'}</TD>
                <TD>
                  {status.checklist ? (
                    <StatusLabel
                      tone={storeStatusTone(status.checklist.status)}
                    >
                      {stageOf(status)}
                    </StatusLabel>
                  ) : (
                    '—'
                  )}
                </TD>
                <TD num>{progressOf(status) || '—'}</TD>
                <TD>
                  <StatusLabel tone={onboardingStatusTone(status)}>
                    {onboardingStatusLabel(status)}
                  </StatusLabel>
                </TD>
                <TD num>{onboardingIssueCount(status)}</TD>
              </TR>
            ))}
          </TBody>
        </Table>
      </div>
    );
  } else {
    body = (
      <EmptyState
        icon={<IconStore />}
        title={filtering ? 'No stores match that search' : 'No stores found.'}
        description={filtering ? 'Try a store name, id, or status.' : undefined}
      />
    );
  }

  return (
    <>
      <ListPage
        title="Store Onboarding Status"
        toolbar={
          <div className="flex min-h-11 items-center px-4 py-2 lg:px-6">
            <TableSearch
              label="Search onboarding statuses"
              placeholder="Search store name, id, or status…"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
            />
          </div>
        }
        actions={
          statuses.length > 0 && (
            <Button
              leading={<IconRefresh size={14} />}
              onClick={() => {
                void fetchStatuses();
              }}
              disabled={loading}
            >
              {loading ? 'Refreshing...' : 'Refresh Statuses'}
            </Button>
          )
        }
      >
        {body}
      </ListPage>

      <StatusDetailsDialog
        open={isDetailsOpen}
        status={selected}
        onClose={() => setIsDetailsOpen(false)}
        onChecklistChange={updateChecklist}
      />
    </>
  );
}
